/** Page data blocks use the same bindings and Insights as other document surfaces. */
import { z } from 'zod';
import { DataBinding, FieldPath } from './binding';
import { Insight } from './insight';
import { InsightResult } from './insight-result';

export const PageBlockPublication = z
  .object({
    enabled: z.boolean().default(false),
    mode: z.enum(['snapshot', 'live']).default('snapshot'),
    /** Explicit output field paths; public tables never infer their columns. */
    columns: z.array(FieldPath).max(30).default([]),
    limit: z.number().int().min(1).max(100).default(20),
  })
  .strict();
export type PageBlockPublication = z.infer<typeof PageBlockPublication>;

export const PageBlockVisibility = z.string().trim().max(4000).nullish();
const common = {
  title: z.string().max(160).default(''),
  visibleWhen: PageBlockVisibility,
  publication: PageBlockPublication.optional(),
};
const RowBinding = DataBinding.superRefine((binding, ctx) => {
  if (binding.kind === 'expression')
    ctx.addIssue({ code: 'custom', message: 'Choose a source that returns records' });
  if ((binding.kind === 'query' || binding.kind === 'relation') && (binding.limit ?? 20) > 100)
    ctx.addIssue({ code: 'custom', path: ['limit'], message: 'Page data blocks support at most 100 rows' });
  const fields = binding.kind === 'static' ? binding.columns : 'fields' in binding ? binding.fields : [];
  if ((fields?.length ?? 0) > 30)
    ctx.addIssue({ code: 'custom', message: 'Page data blocks support at most 30 columns' });
  if (binding.kind === 'static' && binding.rows.length > 100)
    ctx.addIssue({ code: 'custom', path: ['rows'], message: 'Page data blocks support at most 100 rows' });
});

export const LiveViewBlockAttrs = z
  .object({
    ...common,
    binding: RowBinding.nullish(),
    display: z.enum(['table', 'list']).default('table'),
    hideEmpty: z.boolean().default(false),
  })
  .strict()
  .refine((value) => !!value.binding, { path: ['binding'], message: 'Choose a data source' });
export type LiveViewBlockAttrs = z.infer<typeof LiveViewBlockAttrs>;

export const InsightBlockAttrs = z
  .object({ ...common, insight: Insight.nullish(), themeId: z.string().min(1).max(100).nullish() })
  .strict()
  .refine((value) => !!value.insight, { path: ['insight'], message: 'Configure an Insight' });
export type InsightBlockAttrs = z.infer<typeof InsightBlockAttrs>;

export const RepeaterBlockAttrs = z
  .object({ ...common, binding: RowBinding.nullish(), columns: z.number().int().min(1).max(3).default(1) })
  .strict()
  .refine((value) => !!value.binding, { path: ['binding'], message: 'Choose a data source' });
export type RepeaterBlockAttrs = z.infer<typeof RepeaterBlockAttrs>;

/** A materialized public/export chart carries only evaluated data, never its source or filters. */
export const InsightSnapshotAttrs = z
  .object({
    title: z.string().max(160).default(''),
    result: InsightResult.nullish(),
    themeId: z.string().min(1).max(100).nullish(),
  })
  .strict()
  .refine((value) => !!value.result, { path: ['result'], message: 'A snapshot needs evaluated data' });
export type InsightSnapshotAttrs = z.infer<typeof InsightSnapshotAttrs>;
